import { ACTIVITY_MAX_SESSIONS_PER_REQUEST, ACTIVITY_OPEN_DAY_MAX_DAYS } from 'shared';
import type { SessionCreateInput, SessionRepeat } from 'shared';

// Date and time helpers for activity sessions. Staff type dates and times as
// they read them on the wall, so everything here works in the browser's local
// time and converts to an RFC3339 instant only at the edge.

const pad = (n: number): string => String(n).padStart(2, '0');

/** "YYYY-MM-DD" in local time, the value of an <input type="date">. */
export function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "HH:MM" in local time, the value of an <input type="time">. */
export function toTimeInput(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Combine a date input and a time input into a Date in local time. Returns
 * null when either is missing or malformed.
 */
export function combineDateTime(date: string, time: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  const d = new Date(year, month - 1, day, hours, minutes, 0, 0);
  // new Date() rolls 31 February over into March; reject instead.
  if (d.getMonth() !== month - 1 || d.getDate() !== day || d.getHours() !== hours) return null;
  return d;
}

/** Add whole minutes to an "HH:MM" time, stopping at 23:59 rather than wrapping. */
export function addMinutes(time: string, minutes: number): string {
  if (!/^\d{2}:\d{2}$/.test(time)) return time;
  const [h, m] = time.split(':').map(Number);
  const total = Math.min(h * 60 + m + minutes, 23 * 60 + 59);
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

export interface DurationPreset {
  key: string;
  label: string;
  /** Returns the start and end time for a session starting at `start`. */
  apply: (start: string) => { start: string; end: string };
}

/**
 * The lengths activities usually run for. Each only fills in the two time
 * fields, which stay editable: a session is as long as its creator decides.
 */
export const DURATION_PRESETS: DurationPreset[] = [
  { key: '2h', label: '2 horas', apply: (start) => ({ start, end: addMinutes(start, 120) }) },
  { key: 'morning', label: 'Manhã', apply: () => ({ start: '09:00', end: '13:00' }) },
  { key: 'afternoon', label: 'Tarde', apply: () => ({ start: '14:00', end: '18:00' }) },
  { key: 'day', label: 'Dia inteiro', apply: () => ({ start: '09:00', end: '18:00' }) },
];

export const REPEAT_LABELS: Record<SessionRepeat, string> = {
  none: 'Não se repete',
  weekly: 'Todas as semanas',
  biweekly: 'De duas em duas semanas',
};

export interface SessionForm {
  date: string;
  /**
   * The last day of an open day that lasts several; empty when it ends the
   * day it starts, as every session does.
   */
  endDate: string;
  start: string;
  end: string;
  leaderUserId: number | null;
  repeat: SessionRepeat;
  repeatUntil: string;
}

export type SessionFormErrors = Partial<Record<keyof SessionForm, string>>;

/** The day a form's session ends: its end date if it has one, else the day it starts. */
export function lastDay(form: { date: string; endDate: string }): string {
  return form.endDate || form.date;
}

const OPEN_DAY_MAX_MS = ACTIVITY_OPEN_DAY_MAX_DAYS * 24 * 60 * 60_000;

/**
 * What is wrong with when a session ends, or null. `singleEvent` is an open
 * day, the only thing that can end on a later day and so run too long.
 */
export function windowError(
  startsAt: Date,
  endsAt: Date | null,
  singleEvent: boolean,
): string | null {
  if (endsAt === null || endsAt <= startsAt) {
    return singleEvent
      ? 'O Dia Aberto tem de terminar depois de começar.'
      : 'A sessão tem de terminar depois de começar.';
  }
  if (singleEvent && endsAt.getTime() - startsAt.getTime() > OPEN_DAY_MAX_MS) {
    return `Um Dia Aberto dura no máximo ${ACTIVITY_OPEN_DAY_MAX_DAYS} dias.`;
  }
  return null;
}

/**
 * Client-side check of the new-session form. Mirrors the API's rules.
 * `singleEvent` says the form is for an open day rather than a session.
 */
export function validateSessionForm(form: SessionForm, singleEvent = false): SessionFormErrors {
  const errors: SessionFormErrors = {};

  const startsAt = combineDateTime(form.date, form.start);
  const endsAt = combineDateTime(lastDay(form), form.end);

  if (!form.date) errors.date = 'A data é obrigatória.';
  else if (startsAt === null) errors.date = 'Data ou hora de início inválida.';

  if (!form.start) errors.start = 'A hora de início é obrigatória.';
  if (!form.end) errors.end = 'A hora de fim é obrigatória.';
  else if (startsAt !== null) {
    const problem = windowError(startsAt, endsAt, singleEvent);
    if (problem) errors.end = problem;
  }

  if (form.repeat !== 'none') {
    if (!form.repeatUntil) errors.repeatUntil = 'Indica até quando a sessão se repete.';
    else if (form.date && form.repeatUntil < form.date) {
      errors.repeatUntil = 'Tem de ser posterior à primeira sessão.';
    }
  }

  return errors;
}

/** A new-session form as it opens: today, mid-afternoon, not repeating. */
export function emptySessionForm(leaderUserId: number | null, today = new Date()): SessionForm {
  return {
    date: toDateInput(today),
    endDate: '',
    start: '15:00',
    end: '17:00',
    leaderUserId,
    repeat: 'none',
    repeatUntil: '',
  };
}

// The API reports problems against its own field names.
const SERVER_FIELD: Record<string, keyof SessionForm> = {
  startsAt: 'start',
  endsAt: 'end',
  leaderUserId: 'leaderUserId',
  repeat: 'repeat',
  repeatUntil: 'repeatUntil',
};

/** The API's validation errors that are about the session, by form field. */
export function sessionServerErrors(errors: Record<string, string>): SessionFormErrors {
  return Object.entries(errors).reduce<SessionFormErrors>((acc, [field, message]) => {
    const key = SERVER_FIELD[field];
    return key ? { ...acc, [key]: message } : acc;
  }, {});
}

/** Build the API body from a form that passed validateSessionForm. */
export function toSessionCreateInput(form: SessionForm): SessionCreateInput {
  const startsAt = combineDateTime(form.date, form.start);
  const endsAt = combineDateTime(lastDay(form), form.end);
  if (startsAt === null || endsAt === null) {
    throw new Error('toSessionCreateInput: the form has not been validated');
  }
  return {
    startsAt: startsAt.toISOString(),
    endsAt: endsAt.toISOString(),
    ...(form.leaderUserId !== null ? { leaderUserId: form.leaderUserId } : {}),
    repeat: form.repeat,
    ...(form.repeat !== 'none' ? { repeatUntil: form.repeatUntil } : {}),
  };
}

/**
 * How many sessions a repeat rule creates, for the preview under the form.
 * Counts occurrences from `date` up to and including `repeatUntil`.
 */
export function countOccurrences(date: string, repeat: SessionRepeat, repeatUntil: string): number {
  const first = combineDateTime(date, '12:00');
  if (first === null) return 0;
  if (repeat === 'none') return 1;
  const last = combineDateTime(repeatUntil, '12:00');
  if (last === null || last < first) return 0;

  const stepDays = repeat === 'weekly' ? 7 : 14;
  // Noon to noon keeps the day count exact across a daylight-saving change.
  const days = Math.round((last.getTime() - first.getTime()) / 86_400_000);
  return Math.floor(days / stepDays) + 1;
}

/** True when the repeat in `form` creates more sessions than the API takes. */
export function tooManySessions(form: SessionForm): boolean {
  return (
    countOccurrences(form.date, form.repeat, form.repeatUntil) > ACTIVITY_MAX_SESSIONS_PER_REQUEST
  );
}

export const TOO_MANY_SESSIONS_MESSAGE = `A repetição cria mais de ${ACTIVITY_MAX_SESSIONS_PER_REQUEST} sessões. Escolhe uma data mais próxima.`;

/** "qua., 07/10/2026" */
export function formatSessionDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-PT', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** "15:00–17:00" */
export function formatSessionTimes(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  return `${start.toLocaleTimeString('pt-PT', opts)}–${end.toLocaleTimeString('pt-PT', opts)}`;
}

/** Whether a session ends on a later day than it starts, as an open day may. */
export function spansDays(startsAt: string, endsAt: string): boolean {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  return toDateInput(start) !== toDateInput(end);
}

/** "07/10, 15:00" — a moment in something that lasts more than a day. */
export function formatDayTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
  const time = d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time}`;
}

/**
 * From when to when, without the year: "15:00–17:00", or for an open day
 * over several days "10/10, 09:00 – 11/10, 17:00".
 */
export function formatSessionSpan(startsAt: string, endsAt: string): string {
  if (!spansDays(startsAt, endsAt)) return formatSessionTimes(startsAt, endsAt);
  return `${formatDayTime(startsAt)} – ${formatDayTime(endsAt)}`;
}

/**
 * When a session is, in full: "qua., 07/10/2026 · 15:00–17:00", or for an
 * open day over several days "sáb., 10/10/2026, 09:00 – dom., 11/10/2026, 17:00".
 */
export function formatSessionWhen(startsAt: string, endsAt: string): string {
  if (!spansDays(startsAt, endsAt)) {
    return `${formatSessionDate(startsAt)} · ${formatSessionTimes(startsAt, endsAt)}`;
  }
  const time = (iso: string) =>
    new Date(iso).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  return `${formatSessionDate(startsAt)}, ${time(startsAt)} – ${formatSessionDate(endsAt)}, ${time(endsAt)}`;
}

export type SessionPhase = 'upcoming' | 'open' | 'over';

/** Minutes before its start that a session accepts scans. Mirrors JoinEarlyGrace. */
export const JOIN_EARLY_GRACE_MINUTES = 30;

/**
 * Where a session stands against the clock. `open` means participants can
 * scan the QR now, which starts JOIN_EARLY_GRACE_MINUTES before the session.
 */
export function sessionPhase(startsAt: string, endsAt: string, now = Date.now()): SessionPhase {
  const opens = new Date(startsAt).getTime() - JOIN_EARLY_GRACE_MINUTES * 60_000;
  const closes = new Date(endsAt).getTime();
  if (now >= closes) return 'over';
  if (now >= opens) return 'open';
  return 'upcoming';
}

/**
 * The session to highlight: the one on now, or else the first still to come.
 * Null once every session is over. Sessions arrive in date order.
 */
export function nextSessionId(
  sessions: readonly { id: number; startsAt: string; endsAt: string }[],
  now: number,
): number | null {
  const open = sessions.find((s) => sessionPhase(s.startsAt, s.endsAt, now) === 'open');
  if (open) return open.id;
  const upcoming = sessions.find((s) => sessionPhase(s.startsAt, s.endsAt, now) === 'upcoming');
  return upcoming?.id ?? null;
}
